import { ArrowRight, Wifi, Wind, Tv, ShieldCheck, Utensils, MapPin } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import PropertyContact from "@/components/PropertyContact";
import { CONTACTS } from "@/data/contacts";

const IMAGES = {
  hero: "/images/inn/exterior.jpg",
  doubleRoom: "/images/inn/double_room.jpg",
  twinRoom: "/images/inn/twin_room.jpg",
  restaurant: "/images/inn/restaurant.jpg",
  exterior: "/images/inn/exterior.jpg",
  lounge: "/images/inn/lounge.jpg",
  reception: "/images/inn/reception.jpg",
};

const GALLERY = [
  { src: IMAGES.exterior,   caption: "Hotel Façade",          span: "md:col-span-8", aspect: "aspect-[4/3]" },
  { src: IMAGES.reception,  caption: "Reception",             span: "md:col-span-4", aspect: "aspect-[4/3]" },
  { src: IMAGES.doubleRoom, caption: "AC Double Room",        span: "md:col-span-4", aspect: "aspect-square" },
  { src: IMAGES.twinRoom,   caption: "Twin Room",             span: "md:col-span-4", aspect: "aspect-square" },
  { src: IMAGES.lounge,     caption: "Guest Lounge",          span: "md:col-span-4", aspect: "aspect-square" },
  { src: IMAGES.restaurant, caption: "Pure Veg Restaurant",   span: "md:col-span-12", aspect: "aspect-[21/9]" },
];

const ROOMS = [
  { name: "AC Rooms", desc: "Clean, well-maintained air-conditioned rooms for a comfortable stay in any season." },
  { name: "Non-AC Rooms", desc: "Simple, tidy rooms at an even friendlier price point — everything you need, nothing you don't." },
];

const AMENITIES = [
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: Wind, label: "AC & Non-AC Options" },
  { icon: Tv, label: "Cable TV" },
  { icon: ShieldCheck, label: "24/7 Security" },
  { icon: Utensils, label: "Veg Restaurant" },
  { icon: MapPin, label: "Central Location" },
];

const VEG_HIGHLIGHTS = [
  { name: "Traditional Thali", desc: "A wholesome meal with seasonal vegetables, dal, rice, chapati, and homemade pickle. The kind of food that feels like home." },
  { name: "Dosa Varieties", desc: "Crisp, golden dosas served with fresh coconut chutney and sambar. South Indian comfort done right." },
  { name: "Filter Coffee", desc: "Brewed fresh, served in steel. The kind of coffee that makes mornings worth it." },
];

export default function AmrutInnPage() {
  return (
    <div data-testid="amrut-inn-page">
      {/* ─── HERO ─── */}
      <section className="relative h-[80vh] md:h-screen w-full overflow-hidden">
        <img src={IMAGES.hero} alt="Amrut Inn" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 flex flex-col justify-end h-full p-8 md:p-16 lg:p-24 pb-20 md:pb-28">
          <AnimatedSection delay={0.2}><p className="overline-text text-ivory/50 mb-4">Budget-Friendly Stay</p></AnimatedSection>
          <AnimatedSection delay={0.4}><h1 className="font-serif text-5xl md:text-6xl lg:text-8xl text-ivory font-light tracking-tighter leading-none mb-6">Amrut Inn</h1></AnimatedSection>
          <AnimatedSection delay={0.6}><p className="text-sm md:text-base text-ivory/60 max-w-lg leading-relaxed">32 clean rooms, a vegetarian restaurant, and honest pricing. For families, travellers, and anyone who values a good room at a fair price.</p></AnimatedSection>
        </div>
      </section>

      {/* ─── VALUE INTRO ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection><div className="gold-divider mx-auto mb-10" /></AnimatedSection>
          <AnimatedSection delay={0.1}><h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal font-light tracking-tight leading-tight mb-8">Better quality rooms,<br className="hidden md:block" /> lesser price</h2></AnimatedSection>
          <AnimatedSection delay={0.2}>
            <p className="text-base md:text-lg text-charcoal/60 leading-relaxed max-w-2xl mx-auto">
              Amrut Inn is for people who want clean rooms, good service, and fair rates — without
              the premium price tag. We have 32 rooms (AC and non-AC), an in-house vegetarian
              restaurant, and complimentary breakfast and Wi-Fi. The rooms are well-maintained,
              and that's what our guests appreciate most.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── ROOMS ─── */}
      <section data-testid="inn-rooms" className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/50 mb-6">Rooms</p><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-20">Clean and Comfortable</h2></AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {ROOMS.map((room, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className="border-t border-charcoal/15 pt-8">
                  <h4 className="font-serif text-2xl md:text-3xl text-charcoal font-light tracking-tight mb-4">{room.name}</h4>
                  <p className="text-sm text-charcoal/60 leading-relaxed">{room.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GALLERY ─── */}
      <section data-testid="inn-gallery" className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <AnimatedSection>
            <p className="overline-text text-charcoal/40 mb-6">Gallery</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-16">A Look Inside</h2>
          </AnimatedSection>
          <div className="grid grid-cols-12 gap-3 md:gap-4">
            {GALLERY.map((item, i) => (
              <AnimatedSection key={i} delay={Math.min(i * 0.06, 0.36)} className={`col-span-12 ${item.span}`}>
                <figure className={`group relative overflow-hidden ${item.aspect}`}>
                  <img
                    src={item.src}
                    alt={item.caption}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                  />
                  <figcaption className="absolute bottom-0 left-0 right-0 px-5 py-4 bg-gradient-to-t from-charcoal/70 via-charcoal/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="text-[11px] tracking-[0.2em] uppercase text-ivory/90 font-medium">{item.caption}</span>
                  </figcaption>
                </figure>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AMENITIES ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-charcoal/[0.02]">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/40 mb-6">What's Included</p><div className="gold-divider mb-16" /></AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-12 md:gap-10">
            {AMENITIES.map((a, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="text-center"><a.icon size={28} strokeWidth={1} className="text-charcoal/40 mx-auto mb-4" /><p className="text-xs text-charcoal/60 tracking-wide">{a.label}</p></div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHO STAYS HERE ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection>
            <p className="overline-text text-charcoal/50 mb-6">Our Guests</p>
            <p className="text-base text-charcoal/60 leading-relaxed max-w-xl mx-auto">
              Families, frequent travellers, transit officers, long-stay business guests —
              anyone who wants a clean, reliable place to rest without overspending.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── VEGETARIAN RESTAURANT ─── */}
      <section data-testid="inn-veg-restaurant" className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/40 mb-6">In-House Dining</p><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-8">Vegetarian Restaurant</h2></AnimatedSection>
          <AnimatedSection delay={0.1}><p className="text-base text-charcoal/60 leading-relaxed max-w-2xl mb-16">Home-style vegetarian food, prepared fresh and served with warmth. Breakfast is included with every stay.</p></AnimatedSection>
          {VEG_HIGHLIGHTS.map((item, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div className="border-t border-charcoal/10 py-8 md:py-10 grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 md:gap-12">
                <h4 className="font-serif text-xl md:text-2xl text-charcoal font-light tracking-tight">{item.name}</h4>
                <p className="text-sm text-charcoal/50 leading-relaxed">{item.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className="py-12 md:py-16 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><PropertyContact contact={CONTACTS.inn} variant="light" /></AnimatedSection>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 bg-ivory text-center">
        <div className="max-w-[700px] mx-auto">
          <AnimatedSection><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-6">Book a Room</h2></AnimatedSection>
          <AnimatedSection delay={0.1}><p className="text-sm text-charcoal/55 leading-relaxed mb-10 max-w-md mx-auto">Call or WhatsApp us for availability and rates.</p></AnimatedSection>
          <AnimatedSection delay={0.2}>
            <a href={`https://wa.me/${CONTACTS.inn.whatsappRaw}?text=I'd%20like%20to%20book%20a%20room%20at%20Amrut%20Inn`} target="_blank" rel="noopener noreferrer" data-testid="enquire-now-btn" className="inline-flex items-center gap-3 px-10 py-4 border border-charcoal/25 text-charcoal text-xs tracking-[0.2em] uppercase font-medium hover:bg-charcoal hover:text-ivory transition-all duration-500">
              Enquire Now <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
